/**
 * Cloud Provider Schema Definitions
 *
 * Spec-first schema definitions for cloud-native hook/tool/HITL provider.
 * These schemas drive type generation, validation, and query library types.
 *
 * Also the source of truth for the CloudProviderDO WebSocket wire protocol.
 * Frame schemas below are the sole authority for field names and shapes on
 * that wire; the Go bridge (lib/agency/pkg/bridge/cloud_provider_bridge.go)
 * decodes exactly these fields.
 */

import { z } from "zod";
import { hook } from "@pineseed/agency-proto";
import { UuidField, TimestampField } from "./common";

// ---------------------------------------------------------------------------
// Proto-derived constraints — read from the generated agency.hook.v1 reflection
// info (enum values and buf.validate rules), never copied.
// ---------------------------------------------------------------------------

type ProtoField = (typeof hook.HookConfig.fields)[number];
interface ProtoMessageType {
  typeName: string;
  fields: readonly ProtoField[];
}

function protoField(type: ProtoMessageType, name: string): ProtoField {
  const field = type.fields.find((f) => f.name === name);
  if (!field) throw new Error(`${type.typeName} has no field ${name}`);
  return field;
}

const BufEnumConstraint = z.object({
  definedOnly: z.literal(true),
  notIn: z.array(z.number().int()).optional(),
});

const BufEnumRule = z.object({
  "buf.validate.field": z.object({ enum: BufEnumConstraint }),
});

const BufRepeatedEnumRule = z.object({
  "buf.validate.field": z.object({
    repeated: z.object({
      items: z.object({ enum: BufEnumConstraint }),
    }),
  }),
});

const BufInt32Range = z.object({
  "buf.validate.field": z.object({
    int32: z.object({ gte: z.number().int(), lte: z.number().int() }),
  }),
});

/** buf.validate 64-bit lengths/counts, emitted as decimal strings. */
const BufUint64 = z.string().regex(/^[0-9]+$/).transform(Number);

const BufStringRule = z.object({
  "buf.validate.field": z.object({
    string: z.object({
      minLen: BufUint64.optional(),
      maxLen: BufUint64,
      pattern: z.string(),
    }),
  }),
});

const BufRepeatedMaxItems = z.object({
  "buf.validate.field": z.object({
    repeated: z.object({ maxItems: BufUint64 }),
  }),
});

/** A string field's buf.validate rule (min/max length, pattern) as a zod string. */
function protoString(type: ProtoMessageType, fieldName: string): z.ZodString {
  const rule = BufStringRule.parse(protoField(type, fieldName).options)[
    "buf.validate.field"
  ].string;
  const bounded = z.string().max(rule.maxLen).regex(new RegExp(rule.pattern));
  return rule.minLen === undefined ? bounded : bounded.min(rule.minLen);
}

/**
 * Accepted values of a proto enum field, by protojson name (shared prefix
 * included, e.g. HOOK_POINT_PRE_TOOL_CALL): the generated enum values minus
 * the field's buf.validate `not_in` (a repeated field's `repeated.items.enum`
 * rule). A field without `not_in` excludes nothing.
 */
function protoEnum(type: ProtoMessageType, fieldName: string) {
  const field = protoField(type, fieldName);
  if (field.kind !== "enum") {
    throw new Error(`${type.typeName}.${fieldName} is not an enum field`);
  }
  const [, values, prefix] = field.T();
  const rule = field.repeat
    ? BufRepeatedEnumRule.parse(field.options)["buf.validate.field"].repeated
        .items.enum
    : BufEnumRule.parse(field.options)["buf.validate.field"].enum;
  const notIn = rule.notIn ?? [];
  const numbers = new Map(
    Object.entries(values)
      .filter((e): e is [string, number] => typeof e[1] === "number")
      .filter(([, n]) => !notIn.includes(n))
      .map(([key, n]) => [`${prefix ?? ""}${key}`, n] as const),
  );
  return {
    schema: z.enum([...numbers.keys()] as [string, ...string[]]),
    number(name: string): number {
      const n = numbers.get(name);
      if (n === undefined) {
        throw new Error(`${type.typeName}.${fieldName}: unknown value ${name}`);
      }
      return n;
    },
  };
}

// HookConfig.points' item rule defines the accepted HookPoint set
// (UNSPECIFIED excluded).
const HookPointEnum = protoEnum(hook.HookConfig, "points");
const HitlOnTimeoutPolicyEnum = protoEnum(hook.HITLConfig, "on_timeout_policy");
const HitlGatingScopeEnum = protoEnum(hook.HITLConfig, "gating_scope");
const HitlSteeringScopeEnum = protoEnum(hook.HITLConfig, "steering_scope");

const HOOK_PRIORITY_RANGE = BufInt32Range.parse(
  protoField(hook.HookConfig, "priority").options,
)["buf.validate.field"].int32;

const TOOL_FILTER_MAX_ITEMS = BufRepeatedMaxItems.parse(
  protoField(hook.ToolFilter, "tools").options,
)["buf.validate.field"].repeated.maxItems;

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

const PreferredMode = z.enum(["sync", "async"]);
const DeliveryMode = z.enum(["sync", "async", "durable"]);
const TimeoutPolicy = z.enum(["abort", "skip_proceed"]);
const HitlHandlerType = z.enum(["email", "webhook"]);

/**
 * Hook/HITL decision outcome. Mirrors proto enum agency.hook.v1.HITLOutcome
 * (lib/proto/agency/hook/v1/hook.proto). No value outside this set is
 * representable on the wire — unknown strings are rejected at the boundary,
 * never coerced to a default.
 */
const HitlOutcome = z.enum(["approved", "denied", "steered", "escalated"]);

/** Steering injection scope. Mirrors proto enum HITLSteeringScope. */
const HitlSteeringScope = z.enum(["turn", "session"]);

/**
 * Behaviour when a HITL approval is not received in time. Mirrors proto enum
 * agency.hook.v1.HITLOnTimeoutPolicy, which is the source of truth. The proto
 * marks UNSPECIFIED as not_in:[0], so it is not representable here.
 */
const HitlOnTimeoutPolicy = z.enum(["auto_deny", "auto_approve", "escalate"]);

/** Proto agency.hook.v1.HookPoint name, e.g. HOOK_POINT_PRE_TOOL_CALL. */
const HookPointName = HookPointEnum.schema;

const HookPriority = z
  .number()
  .int()
  .min(HOOK_PRIORITY_RANGE.gte)
  .max(HOOK_PRIORITY_RANGE.lte);

/**
 * A tool a hook applies to. Mirrors proto agency.hook.v1.ToolNameFilter;
 * location null = any location (the proto's empty string).
 */
const ToolNameFilter = z.object({
  name: protoString(hook.ToolNameFilter, "name"),
  location: protoString(hook.ToolNameFilter, "location").min(1).nullable(),
});

/** Proto ToolFilter.tools (OR semantics); empty = all tools at all locations. */
const ToolFilters = z.array(ToolNameFilter).max(TOOL_FILTER_MAX_ITEMS);

/** The only points a ToolFilter applies at (proto ToolFilter). */
const ToolFilterHookPoints = HookPointName.options.filter((point) =>
  [hook.HookPoint.PRE_TOOL_CALL, hook.HookPoint.POST_TOOL_CALL].includes(
    HookPointEnum.number(point),
  ),
);

/**
 * A non-empty toolFilters is only valid when every point is a tool point:
 * elsewhere the harness evaluates a ToolFilter as false (owner Q49).
 */
function toolFiltersAtToolPoints<
  T extends z.ZodType<{ points: string[]; toolFilters: unknown[] }>,
>(schema: T): T {
  return schema.refine(
    (h) =>
      h.toolFilters.length === 0 ||
      h.points.every((point) => ToolFilterHookPoints.includes(point)),
    {
      path: ["toolFilters"],
      message: `toolFilters apply only at ${ToolFilterHookPoints.join(", ")}`,
    },
  );
}

/**
 * Human-in-the-loop approval config of a hook definition. Mirrors proto
 * agency.hook.v1.HITLConfig; enum values are the proto names, and the
 * required `timeout` Duration is held as whole seconds.
 */
const HitlConfig = z.object({
  enabled: z.boolean(),
  onTimeoutPolicy: HitlOnTimeoutPolicyEnum.schema,
  gatingScope: HitlGatingScopeEnum.schema,
  timeoutSeconds: z.number().int().positive(),
  steeringScope: HitlSteeringScopeEnum.schema,
});

/**
 * A reason a human supplied. "timeout" is reserved: the bridge drops HITL
 * results carrying it, and timeouts are enforced by the harness, not the API.
 */
const HumanReason = z
  .string()
  .refine((reason) => reason !== "timeout", {
    message: 'reason "timeout" is reserved',
  });

// ---------------------------------------------------------------------------
// Core entities — workspace-scoped
// ---------------------------------------------------------------------------

/** Cloud-provider tool descriptor — registered per workspace. */
const CloudProviderTool = z.object({
  accountId: UuidField,
  workspaceId: UuidField,
  handlerName: z.string().min(1).max(255),
  displayName: z.string().min(1).max(255),
  agentName: z.string().min(1).max(255),
  description: z.string().min(1),
  inputSchema: z.string().min(1), // JSON Schema as string
  version: z.string().min(1).max(64),
  timeoutSeconds: z.number().int().positive(),
  preferredMode: PreferredMode,
  tags: z.string().nullable(), // JSON array as string
  category: z.string().max(255).nullable(),
  createdAt: TimestampField,
  updatedAt: TimestampField,
});

/**
 * Hook descriptor fields — registered per workspace. Unrefined so derived
 * schemas can omit/partial it; every complete hook schema applies
 * toolFiltersAtToolPoints.
 */
const HookFields = z.object({
  accountId: UuidField,
  workspaceId: UuidField,
  name: z.string().min(1).max(255),
  points: z.array(HookPointName).min(1),
  priority: HookPriority,
  timeoutPolicy: TimeoutPolicy,
  deliveryMode: DeliveryMode,
  timeoutSeconds: z.number().positive(),
  enabled: z.boolean(),
  /** Tools the hook applies to; empty = all tools. */
  toolFilters: ToolFilters,
  /** null = the hook needs no human approval. */
  hitlConfig: HitlConfig.nullable(),
  createdAt: TimestampField,
  updatedAt: TimestampField,
});

/** Cloud-provider hook descriptor — registered per workspace. */
const CloudProviderHook = toolFiltersAtToolPoints(HookFields);

/**
 * Cloud-provider HITL handler definition — registered per workspace.
 * handlerConfig is JSON text whose shape depends on handlerType
 * (HITL_HANDLER_CONFIG); unrefined so derived schemas can omit/partial it,
 * every write applies hitlConfigMatchesType.
 */
const CloudProviderHitlHandler = z.object({
  accountId: UuidField,
  workspaceId: UuidField,
  name: z.string().min(1).max(255),
  handlerType: HitlHandlerType,
  handlerConfig: z.string().min(1),
  timeoutSeconds: z.number().int().positive(),
  createdAt: TimestampField,
  updatedAt: TimestampField,
});

/** handlerConfig of a webhook handler (R66 Q10a): POST target and HMAC signing secret. */
/** An absolute http(s) URL (any host, loopback included). */
const HttpUrl = z.url({ protocol: /^https?$/ });

const WebhookHitlHandlerConfig = z.object({
  url: HttpUrl,
  signingSecret: z.string().min(1),
});

const JsonText = z.string().transform((text, ctx) => {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    ctx.addIssue({ code: "custom", message: "must be JSON text" });
    return z.NEVER;
  }
});

/** handlerConfig validation per handlerType; email stays opaque JSON text (next task). */
const HITL_HANDLER_CONFIG = {
  webhook: JsonText.pipe(WebhookHitlHandlerConfig),
  email: z.string().min(1),
} as const satisfies Record<z.infer<typeof HitlHandlerType>, z.ZodType>;

/** handlerConfig must be valid for handlerType. */
function hitlConfigMatchesType<
  T extends z.ZodType<{
    handlerType: z.infer<typeof HitlHandlerType>;
    handlerConfig: string;
  }>,
>(schema: T): T {
  return schema.superRefine((h, ctx) => {
    const result = HITL_HANDLER_CONFIG[h.handlerType].safeParse(h.handlerConfig);
    if (result.success) return;
    for (const issue of result.error.issues) {
      ctx.addIssue({
        code: "custom",
        path: ["handlerConfig", ...issue.path],
        message: `${h.handlerType} handlerConfig: ${issue.message}`,
      });
    }
  });
}

/** A stored webhook handler's config; throws when it violates the contract. */
function parseWebhookHitlHandlerConfig(
  handlerConfig: string,
): z.infer<typeof WebhookHitlHandlerConfig> {
  return HITL_HANDLER_CONFIG.webhook.parse(handlerConfig);
}

// ---------------------------------------------------------------------------
// Core entities — tenant-scoped (no workspaceId)
// ---------------------------------------------------------------------------

/** Tenant-scoped tool descriptor — applies to all workspaces in the account. */
const CloudProviderTenantTool = CloudProviderTool.omit({ workspaceId: true });

/** Tenant-scoped hook descriptor — applies to all workspaces in the account. */
const CloudProviderTenantHook = toolFiltersAtToolPoints(
  HookFields.omit({ workspaceId: true }),
);

/** Tenant-scoped HITL handler definition — applies to all workspaces in the account. */
const CloudProviderTenantHitlHandler = CloudProviderHitlHandler.omit({
  workspaceId: true,
});

// ---------------------------------------------------------------------------
// Create/Update request schemas — derived from the workspace entities
// (shared by workspace and tenant scope). Update never changes the key.
// ---------------------------------------------------------------------------

/** Entity fields owned by the server (path scope and timestamps). */
const SERVER_OWNED = {
  accountId: true,
  workspaceId: true,
  createdAt: true,
  updatedAt: true,
} as const;

const CreateCloudProviderToolRequest = CloudProviderTool.omit(
  SERVER_OWNED,
).partial({ tags: true, category: true });

const UpdateCloudProviderToolRequest = CreateCloudProviderToolRequest.omit({
  handlerName: true,
}).partial();

const CreateCloudProviderHookRequest = toolFiltersAtToolPoints(
  HookFields.omit(SERVER_OWNED),
);

const HookSettingsFields = HookFields.omit({ ...SERVER_OWNED, name: true });

const UpdateCloudProviderHookRequest = HookSettingsFields.partial();

/**
 * A hook's settings (no key, no server-owned fields): what storage writes on
 * create and on update, the latter after merging the partial update into the
 * stored hook.
 */
const HookSettings = toolFiltersAtToolPoints(HookSettingsFields);

/** Hook settings that violate the spec — a client error (400). */
class HookSettingsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "HookSettingsError";
  }
}

/** Validated hook settings; throws HookSettingsError when invalid. */
function parseHookSettings(settings: unknown): z.infer<typeof HookSettings> {
  const result = HookSettings.safeParse(settings);
  if (!result.success) throw new HookSettingsError(z.prettifyError(result.error));
  return result.data;
}

const CreateCloudProviderHitlHandlerRequest = hitlConfigMatchesType(
  CloudProviderHitlHandler.omit(SERVER_OWNED),
);

const HitlHandlerSettingsFields = CloudProviderHitlHandler.omit({
  ...SERVER_OWNED,
  name: true,
});

const UpdateCloudProviderHitlHandlerRequest =
  HitlHandlerSettingsFields.partial();

/**
 * A HITL handler's settings (no key, no server-owned fields): what storage
 * writes on create and on update, the latter after merging the partial
 * update into the stored handler.
 */
const HitlHandlerSettings = hitlConfigMatchesType(HitlHandlerSettingsFields);

/** HITL handler settings that violate the spec — a client error (400). */
class HitlHandlerSettingsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "HitlHandlerSettingsError";
  }
}

/** Validated HITL handler settings; throws HitlHandlerSettingsError when invalid. */
function parseHitlHandlerSettings(
  settings: unknown,
): z.infer<typeof HitlHandlerSettings> {
  const result = HitlHandlerSettings.safeParse(settings);
  if (!result.success) {
    throw new HitlHandlerSettingsError(z.prettifyError(result.error));
  }
  return result.data;
}

// ---------------------------------------------------------------------------
// Event schemas — CRUD lifecycle events
// ---------------------------------------------------------------------------

const ToolCreateEvent = z.object({
  action: z.literal("created"),
  tool: CloudProviderTool,
  timestamp: TimestampField,
});

const ToolUpdateEvent = z.object({
  action: z.literal("updated"),
  tool: CloudProviderTool,
  timestamp: TimestampField,
});

const ToolDeleteEvent = z.object({
  action: z.literal("deleted"),
  accountId: UuidField,
  workspaceId: UuidField,
  handlerName: z.string(),
  timestamp: TimestampField,
});

const HookCreateEvent = z.object({
  action: z.literal("created"),
  hook: CloudProviderHook,
  timestamp: TimestampField,
});

const HookUpdateEvent = z.object({
  action: z.literal("updated"),
  hook: CloudProviderHook,
  timestamp: TimestampField,
});

const HookDeleteEvent = z.object({
  action: z.literal("deleted"),
  accountId: UuidField,
  workspaceId: UuidField,
  name: z.string(),
  timestamp: TimestampField,
});

const HitlHandlerCreateEvent = z.object({
  action: z.literal("created"),
  hitlHandler: CloudProviderHitlHandler,
  timestamp: TimestampField,
});

const HitlHandlerUpdateEvent = z.object({
  action: z.literal("updated"),
  hitlHandler: CloudProviderHitlHandler,
  timestamp: TimestampField,
});

const HitlHandlerDeleteEvent = z.object({
  action: z.literal("deleted"),
  accountId: UuidField,
  workspaceId: UuidField,
  name: z.string(),
  timestamp: TimestampField,
});

// ---------------------------------------------------------------------------
// Tenant event schemas — CRUD lifecycle events (no workspaceId)
// ---------------------------------------------------------------------------

const TenantToolCreateEvent = z.object({
  action: z.literal("created"),
  tool: CloudProviderTenantTool,
  timestamp: TimestampField,
});

const TenantToolUpdateEvent = z.object({
  action: z.literal("updated"),
  tool: CloudProviderTenantTool,
  timestamp: TimestampField,
});

const TenantToolDeleteEvent = z.object({
  action: z.literal("deleted"),
  accountId: UuidField,
  handlerName: z.string(),
  timestamp: TimestampField,
});

const TenantHookCreateEvent = z.object({
  action: z.literal("created"),
  hook: CloudProviderTenantHook,
  timestamp: TimestampField,
});

const TenantHookUpdateEvent = z.object({
  action: z.literal("updated"),
  hook: CloudProviderTenantHook,
  timestamp: TimestampField,
});

const TenantHookDeleteEvent = z.object({
  action: z.literal("deleted"),
  accountId: UuidField,
  name: z.string(),
  timestamp: TimestampField,
});

const TenantHitlHandlerCreateEvent = z.object({
  action: z.literal("created"),
  hitlHandler: CloudProviderTenantHitlHandler,
  timestamp: TimestampField,
});

const TenantHitlHandlerUpdateEvent = z.object({
  action: z.literal("updated"),
  hitlHandler: CloudProviderTenantHitlHandler,
  timestamp: TimestampField,
});

const TenantHitlHandlerDeleteEvent = z.object({
  action: z.literal("deleted"),
  accountId: UuidField,
  name: z.string(),
  timestamp: TimestampField,
});

// ---------------------------------------------------------------------------
// WebSocket wire frames — CloudProviderDO ↔ Go bridge
//
// Source of truth for the frame protocol. Every frame the DO emits is
// validated against these schemas before send; every field here is decoded
// by lib/agency/pkg/bridge/cloud_provider_handlers.go.
//
// Error convention is `isError` + `errorMessage`, matching proto
// agency.tool.v1.ToolResult{is_error, error_message} and acp-schemas.ts.
// ---------------------------------------------------------------------------

/** Correlation id is opaque — harness call ids are not UUIDs. */
const CorrelationId = z.string().min(1);

/** Outbound: tool execution result, DO → bridge. */
const ToolResultFrame = z.object({
  type: z.literal("tool_result"),
  correlationId: CorrelationId,
  toolName: z.string().min(1),
  result: z.record(z.string(), z.unknown()),
  isError: z.boolean(),
  errorMessage: z.string(),
});

/** Outbound: hook decision, DO → bridge. */
const HookResultFrame = z.object({
  type: z.literal("hook_result"),
  correlationId: CorrelationId,
  hookName: z.string().min(1),
  point: z.string().min(1),
  outcome: HitlOutcome,
  reason: z.string(),
  steering: z.string(),
  steeringScope: HitlSteeringScope,
});

/** Outbound: HITL decision, DO → bridge. Never a timeout outcome. */
const HitlResultFrame = z.object({
  type: z.literal("hitl_result"),
  correlationId: CorrelationId,
  outcome: HitlOutcome,
  reason: HumanReason,
});

/** Outbound: liveness, DO → bridge. The reply to every `tick`. */
const HeartbeatFrame = z.object({
  type: z.literal("heartbeat"),
  timestamp: z.number().int().nonnegative(),
});

/**
 * HITL config as proto3 JSON of agency.hook.v1.HITLConfig (the bridge decodes
 * it with protojson). `timeout` is a Duration string such as "3600s".
 */
const HitlConfigProtoJson = z.object({
  enabled: z.boolean(),
  onTimeoutPolicy: HitlOnTimeoutPolicyEnum.schema,
  gatingScope: HitlGatingScopeEnum.schema,
  timeout: z.string().regex(/^[0-9]+s$/),
  steeringScope: HitlSteeringScopeEnum.schema,
});

/** Register frame hook entry, decoded by the bridge into proto HookConfig. */
const RegisterHookEntry = z.object({
  hookName: z.string().min(1),
  points: z.array(HookPointName).min(1),
  priority: HookPriority,
  timeoutSeconds: z.number().positive(),
  timeoutPolicy: TimeoutPolicy,
  deliveryMode: DeliveryMode,
  enabled: z.boolean(),
  /** Decoded by the bridge into ToolNameFilter; null location = "" (any). */
  toolFilters: ToolFilters,
  /** null = the hook needs no human approval. */
  hitlConfig: HitlConfigProtoJson.nullable(),
});

/**
 * Outbound: the full definition set, DO → bridge, sent in reply to `ready`.
 * The bridge registers each item independently and answers registration_ack.
 */
const CloudProviderRegisterFrame = z.object({
  type: z.literal("cloud_provider_register"),
  workspaceId: z.uuid(),
  processId: z.string().min(1),
  location: z.string().min(1),
  /** Each descriptor is a base64-encoded binary agency.tool.v1.ToolDescriptor. */
  tools: z.array(z.object({ descriptor: z.string().min(1) })),
  hooks: z.array(RegisterHookEntry),
  /** Names only — handlerConfig may carry credentials and never leaves the DO. */
  hitlHandlers: z.array(z.object({ name: z.string().min(1) })),
});

/** Inbound: liveness probe, bridge → DO. Answered by `heartbeat`. */
const TickFrame = z.object({
  type: z.literal("tick"),
});

/** Inbound: the bridge is (re)connected and the harness is up, bridge → DO. */
const ReadyFrame = z.object({
  type: z.literal("ready"),
});

/** An item of a register frame the bridge could not register. */
const RegistrationRejection = z.object({
  kind: z.enum(["tool", "hook", "hitlHandler"]),
  name: z.string().min(1),
  error: z.string().min(1),
});

const RegistrationAckFields = {
  type: z.literal("registration_ack"),
  rejected: z.array(RegistrationRejection),
};

/**
 * Inbound: registration outcome, bridge → DO. heartbeatIntervalMs is present
 * iff something was registered.
 */
const RegistrationAckFrame = z.union([
  z.strictObject({
    ...RegistrationAckFields,
    registeredCount: z.literal(0),
  }),
  z.strictObject({
    ...RegistrationAckFields,
    registeredCount: z.number().int().positive(),
    heartbeatIntervalMs: z.number().int().positive(),
  }),
]);

/**
 * Inbound: HITL approval request, bridge → DO.
 *
 * deadlineAtMs is the harness gate's absolute deadline (epoch ms); the harness
 * enforces it. No HITL handler is named: nothing in the hook protos links a
 * hook to a specific handler, so the DO fans out to every registered handler.
 */
const HitlRequestFrame = z.object({
  type: z.literal("hitl_request"),
  correlationId: CorrelationId,
  hitlId: z.string().min(1),
  sessionId: z.string().min(1),
  hookName: z.string().min(1),
  point: z.string().min(1),
  tenant: z.string().min(1),
  workspace: z.string().min(1),
  userSub: z.string(),
  deadlineAtMs: z.number().int().positive(),
  /** Display-only: the harness applies the policy; the API must not. */
  onTimeoutPolicy: HitlOnTimeoutPolicy,
});

/** Inbound: hook invocation, bridge → DO. */
const HookDispatchFrame = z.object({
  type: z.literal("hook_dispatch"),
  correlationId: CorrelationId,
  hookName: z.string().min(1),
  point: HookPointName,
  sessionId: z.string(),
  /** Proto-JSON of the hook event payload (null when absent), opaque here. */
  data: z.json(),
  /** true when the harness awaits the hook_result (request/reply). */
  sync: z.boolean(),
});

/** Inbound: tool call, bridge → DO. */
const ToolExecFrame = z.object({
  type: z.literal("tool_exec"),
  correlationId: CorrelationId,
  toolName: z.string().min(1),
  callId: z.string().min(1),
  sessionId: z.string(),
  /** null when the call carries no arguments. */
  arguments: z.record(z.string(), z.unknown()).nullable(),
});

/** Inbound: observability event to persist, bridge → DO. */
const TraceEventFrame = z.object({
  type: z.literal("trace_event"),
  sessionId: z.string().min(1),
  seq: z.number().int().positive(),
  eventType: z.string().min(1),
  eventTime: z.iso.datetime({ offset: true }),
  subject: z.string().min(1),
  summary: z.string().min(1),
  detail: z.string().optional(),
});

const CloudProviderInboundFrame = z.union([
  TickFrame,
  ReadyFrame,
  RegistrationAckFrame,
  HitlRequestFrame,
  HookDispatchFrame,
  ToolExecFrame,
  TraceEventFrame,
]);

const CloudProviderOutboundFrame = z.discriminatedUnion("type", [
  ToolResultFrame,
  HookResultFrame,
  HitlResultFrame,
  HeartbeatFrame,
  CloudProviderRegisterFrame,
]);

/** HITL resolution submitted with a signed HITL link (R66). */
const HitlResolveRequest = z.object({
  outcome: HitlOutcome,
  reason: HumanReason.optional(),
});

/**
 * The JSON body of every HITL webhook POST (R66 Q12a). resolveUrl (the
 * approval page) and resolveApiUrl (the resolve API) are the same
 * `?token=` URL: GET serves the page, POST resolves.
 */
const HitlWebhookNotification = z.object({
  correlationId: CorrelationId,
  workspaceId: z.uuid(),
  hookName: z.string().min(1),
  point: z.string().min(1),
  sessionId: z.string().min(1),
  round: z.number().int().positive(),
  deadlineAtMs: z.number().int().positive(),
  userSub: z.string(),
  resolveUrl: HttpUrl,
  resolveApiUrl: HttpUrl,
});

type HookDefinition = z.infer<typeof CloudProviderTenantHook>;

/**
 * The single mapping from a stored hook definition (workspace or tenant) to
 * its register frame entry. hitlConfig goes through the generated proto
 * HITLConfig type and its protojson writer.
 */
function toRegisterHookEntry(
  definition: HookDefinition,
): z.infer<typeof RegisterHookEntry> {
  const { hitlConfig } = definition;
  return RegisterHookEntry.parse({
    hookName: definition.name,
    points: definition.points,
    priority: definition.priority,
    timeoutSeconds: definition.timeoutSeconds,
    timeoutPolicy: definition.timeoutPolicy,
    deliveryMode: definition.deliveryMode,
    enabled: definition.enabled,
    toolFilters: definition.toolFilters,
    hitlConfig:
      hitlConfig === null
        ? null
        : hook.HITLConfig.toJson(
            hook.HITLConfig.create({
              enabled: hitlConfig.enabled,
              onTimeoutPolicy: HitlOnTimeoutPolicyEnum.number(
                hitlConfig.onTimeoutPolicy,
              ),
              gatingScope: HitlGatingScopeEnum.number(hitlConfig.gatingScope),
              timeout: { seconds: BigInt(hitlConfig.timeoutSeconds), nanos: 0 },
              steeringScope: HitlSteeringScopeEnum.number(
                hitlConfig.steeringScope,
              ),
            }),
            { emitDefaultValues: true },
          ),
  });
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export const schemas = {
  // Enums
  PreferredMode,
  DeliveryMode,
  TimeoutPolicy,
  HitlHandlerType,
  HitlOutcome,
  HitlSteeringScope,
  HitlOnTimeoutPolicy,
  HookPointName,
  HitlConfig,
  ToolNameFilter,

  // Workspace-scoped entities
  CloudProviderTool,
  CloudProviderHook,
  CloudProviderHitlHandler,

  // Tenant-scoped entities
  CloudProviderTenantTool,
  CloudProviderTenantHook,
  CloudProviderTenantHitlHandler,

  // Requests (shared — same shape for workspace and tenant create/update)
  CreateCloudProviderToolRequest,
  UpdateCloudProviderToolRequest,
  CreateCloudProviderHookRequest,
  UpdateCloudProviderHookRequest,
  CreateCloudProviderHitlHandlerRequest,
  UpdateCloudProviderHitlHandlerRequest,
  HookSettings,
  HitlHandlerSettings,
  WebhookHitlHandlerConfig,

  // Workspace-scoped events
  ToolCreateEvent,
  ToolUpdateEvent,
  ToolDeleteEvent,
  HookCreateEvent,
  HookUpdateEvent,
  HookDeleteEvent,
  HitlHandlerCreateEvent,
  HitlHandlerUpdateEvent,
  HitlHandlerDeleteEvent,

  // Tenant-scoped events
  TenantToolCreateEvent,
  TenantToolUpdateEvent,
  TenantToolDeleteEvent,
  TenantHookCreateEvent,
  TenantHookUpdateEvent,
  TenantHookDeleteEvent,
  TenantHitlHandlerCreateEvent,
  TenantHitlHandlerUpdateEvent,
  TenantHitlHandlerDeleteEvent,

  // WebSocket wire frames
  ToolResultFrame,
  HookResultFrame,
  HitlResultFrame,
  HeartbeatFrame,
  HitlConfigProtoJson,
  RegisterHookEntry,
  CloudProviderRegisterFrame,
  CloudProviderOutboundFrame,
  TickFrame,
  ReadyFrame,
  RegistrationRejection,
  RegistrationAckFrame,
  HitlRequestFrame,
  HookDispatchFrame,
  ToolExecFrame,
  TraceEventFrame,
  CloudProviderInboundFrame,
  HitlResolveRequest,
  HitlWebhookNotification,
};

export type SchemaTypes = {
  [K in keyof typeof schemas]: z.infer<(typeof schemas)[K]>;
};

export type { SchemaTypes as CloudProviderSchemaTypes };
export { schemas as cloudProviderSchemas };

export {
  ToolResultFrame,
  HookResultFrame,
  HitlResultFrame,
  HeartbeatFrame,
  RegisterHookEntry,
  CloudProviderRegisterFrame,
  CloudProviderOutboundFrame,
  TickFrame,
  ReadyFrame,
  RegistrationAckFrame,
  HitlRequestFrame,
  HookDispatchFrame,
  ToolExecFrame,
  TraceEventFrame,
  CloudProviderInboundFrame,
  HitlResolveRequest,
  HitlWebhookNotification,
  HitlOutcome,
  HitlSteeringScope,
  HitlOnTimeoutPolicy,
  HookPointName,
  HitlConfig,
  ToolFilterHookPoints,
  HookSettingsError,
  parseHookSettings,
  HitlHandlerSettingsError,
  parseHitlHandlerSettings,
  parseWebhookHitlHandlerConfig,
  toRegisterHookEntry,
};

export type CloudProviderOutboundFrameType = z.infer<
  typeof CloudProviderOutboundFrame
>;
export type CloudProviderInboundFrameType = z.infer<
  typeof CloudProviderInboundFrame
>;
export type CloudProviderRegisterFrameType = z.infer<
  typeof CloudProviderRegisterFrame
>;
export type RegisterHookEntryType = z.infer<typeof RegisterHookEntry>;
export type RegistrationAckFrameType = z.infer<typeof RegistrationAckFrame>;
export type HitlRequestFrameType = z.infer<typeof HitlRequestFrame>;
export type HitlResolveRequestType = z.infer<typeof HitlResolveRequest>;
export type HitlOutcomeType = z.infer<typeof HitlOutcome>;
export type HitlWebhookNotificationType = z.infer<typeof HitlWebhookNotification>;
export type WebhookHitlHandlerConfigType = z.infer<typeof WebhookHitlHandlerConfig>;
export type HitlOnTimeoutPolicyType = z.infer<typeof HitlOnTimeoutPolicy>;
export type HitlConfigType = z.infer<typeof HitlConfig>;
